import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const TRI_LABELS  = { satisfactory: { label: 'Satisfactory', color: 'var(--green)', bg: 'var(--green-bg)' }, improving: { label: 'Improvement Required', color: 'var(--amber)', bg: 'var(--amber-bg)' }, unsatisfactory: { label: 'Unsatisfactory', color: 'var(--red)', bg: 'var(--red-bg)' } };
const BIN_LABELS  = { yes: { label: 'Yes', color: 'var(--green)', bg: 'var(--green-bg)' }, no: { label: 'No', color: 'var(--red)', bg: 'var(--red-bg)' } };

const STAFF_ITEMS   = [['uniforms','Uniforms'],['cleaning','Cleaning'],['punctuality','Punctuality'],['jobPerformance','Job Performance'],['fillingBottleEndOfDay','Filling Bottle End of Day'],['cleaningSiteAfterWork','Cleaning Site After Work']];
const CLEANING_ITEMS= [['floorClean','Floor Clean'],['washpadClean','Washpad Clean'],['binsEmpty','Bins Empty'],['bottlesLabeled','Bottles Labeled'],['highPressureToolsWorking','High Pressure Tools Working'],['wiresJointsCovered','Wires / Joints Covered'],['trolleysClean','Trolleys Clean'],['ragsClean','Rags Clean'],['usedGlovesInBin','Used Gloves in Bin']];
const QUALITY_ITEMS = [['carsDeliveredAsPerStandard','Cars Delivered as per Standard'],['carsDeliveredOnTime','Cars Delivered on Time'],['carsHandledWithCare','Cars Handled with Care'],['carDamagePicsRecorded','Car Damage Pics Recorded'],['carVideosRecordedBeforeWashing','Car Videos Recorded Before Washing'],['carsParkedInDesignatedArea','Cars Parked in Designated Area'],['carKeysReturnedAsDirected','Car Keys Returned as Directed'],['carsWrittenOnSheetWithTimings','Cars Written on Sheet with Timings']];
const MGR_ITEMS     = [['quality','Quality'],['cleaning','Cleaning'],['sitePresentation','Site Presentation'],['communication','Communication'],['invoicesOnTime','Invoices — On Time?'],['staffPunctuality','Staff Punctuality']];

// ── Rating badge ──
const RatingBadge = ({ rating, type = 'tri' }) => {
  if (!rating) return <span style={{ color: 'var(--muted)', fontSize: '.72rem' }}>—</span>;
  const map = type === 'tri' ? TRI_LABELS : BIN_LABELS;
  const s   = map[rating];
  if (!s) return <span style={{ color: 'var(--muted)', fontSize: '.72rem' }}>—</span>;
  return <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: '20px', fontSize: '.72rem', fontWeight: 700 }}>{s.label}</span>;
};

// ── Section display ──
const Section = ({ icon, title, items, data, type, photos, onPhotoClick }) => {
  const filled = items.filter(([k]) => data?.[k]?.rating).length;
  return (
    <div style={{ background: '#ffffff', border: '1px solid var(--border-md)', borderRadius: '14px', overflow: 'hidden', marginBottom: '16px' }}>
      <div style={{ background: 'rgba(0,180,216,0.05)', borderBottom: '1px solid var(--border-md)', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '30px', height: '30px', background: 'var(--cyan)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.95rem', flexShrink: 0 }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: '.9rem' }}>{title}</div>
        </div>
        <span style={{ fontSize: '.7rem', color: 'var(--muted)', fontFamily: "'JetBrains Mono', monospace" }}>{filled}/{items.length} filled</span>
      </div>
      <div style={{ padding: '4px 0' }}>
        {items.map(([key, label]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontSize: '.85rem', color: 'var(--text)', flex: 1, minWidth: '160px' }}>{label}</span>
            <RatingBadge rating={data?.[key]?.rating} type={type}/>
          </div>
        ))}
      </div>
      {/* Photos */}
      {photos?.length > 0 && (
        <div style={{ padding: '12px 18px', borderTop: '1px dashed var(--border)' }}>
          <div style={{ fontSize: '.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: 'var(--muted)', marginBottom: '8px' }}>📸 Photos ({photos.length})</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {photos.map((p, i) => (
              <img key={i} src={p.url} alt={`photo-${i}`} onClick={() => onPhotoClick(photos, i)}
                style={{ width: '70px', height: '70px', borderRadius: '8px', objectFit: 'cover', border: '1.5px solid var(--border)', cursor: 'pointer', transition: 'border-color .15s' }}
                onMouseEnter={e => e.target.style.borderColor='var(--cyan)'}
                onMouseLeave={e => e.target.style.borderColor='var(--border)'}/>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function InspectionDetail() {
  const { id }    = useParams();
  const { user }  = useAuth();
  const navigate  = useNavigate();

  const [insp,        setInsp]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [reviewing,   setReviewing]   = useState(false);
  const [ownerNotes,  setOwnerNotes]  = useState('');
  const [saving,      setSaving]      = useState(false);
  const [lbPhotos,    setLbPhotos]    = useState([]);
  const [lbIndex,     setLbIndex]     = useState(0);
  const [lbOpen,      setLbOpen]      = useState(false);
  

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get(`/inspections/${id}`);
        setInsp(data.inspection);
        setOwnerNotes(data.inspection.ownerNotes || '');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load inspection');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const openLightbox = (photos, idx) => { setLbPhotos(photos); setLbIndex(idx); setLbOpen(true); };

  const handleReview = async (status) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/inspections/${id}/status`, { status, ownerNotes });
      setInsp(data.inspection);
      setReviewing(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete this inspection? This cannot be undone.`)) return;
    try {
      await api.delete(`/inspections/${id}`);
      navigate('/inspections');
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleExportPDF = () => {
    const token   = localStorage.getItem('cs_token');
    const baseURL = process.env.NODE_ENV === 'development'
      ? 'http://localhost:8000'
      : '';
    window.open(`${baseURL}/api/inspections/${id}/pdf?token=${token}`, '_blank');
  };

  if (loading) return <LoadingScreen/>;
  if (error)   return <ErrorScreen error={error} onBack={() => navigate(-1)}/>;
  if (!insp)   return null;

  const statusStyle = { submitted: { bg: 'rgba(245,158,11,.12)', color: '#f59e0b', label: '⏳ Submitted' }, reviewed: { bg: 'rgba(34,197,94,.12)', color: '#22c55e', label: '✅ Reviewed' }, archived: { bg: 'rgba(100,116,139,.12)', color: '#94a3b8', label: '📁 Archived' } };
  const ss = statusStyle[insp.status] || statusStyle.submitted;

  return (
    <div style={{ minHeight: '100vh', background:'var(--bg)', fontFamily: "'Inter', sans-serif" }}>

      {/* Lightbox */}
      {lbOpen && (
        <div onClick={() => setLbOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.93)', zIndex: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>
          <div onClick={e=>e.stopPropagation()} style={{ position: 'absolute', top: '18px', right: '20px', background: 'var(--bg)', border: '1px solid var(--border-md)', color: 'var(--text)', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => setLbOpen(false)}>✕</div>
          <img src={lbPhotos[lbIndex]?.url} alt="" style={{ maxWidth: '92vw', maxHeight: '74vh', borderRadius: '12px', objectFit: 'contain' }}/>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <button onClick={() => setLbIndex(i => (i-1+lbPhotos.length)%lbPhotos.length)} style={lbBtnStyle}>←</button>
            <span style={{ color: 'var(--muted)', fontFamily: "'JetBrains Mono', monospace", fontSize: '.8rem' }}>{lbIndex+1} / {lbPhotos.length}</span>
            <button onClick={() => setLbIndex(i => (i+1)%lbPhotos.length)} style={lbBtnStyle}>→</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ background: '#ffffff', borderBottom: '1.5px solid var(--border)', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => navigate(-1)} style={backBtnStyle}>← Back</button>
        <div>
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: '1rem' }}>Inspection Detail</div>
          <div style={{ color: 'var(--blue)', fontSize: '.7rem' }}>{insp.site} · {new Date(insp.submittedAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ background: ss.bg, color: ss.color, padding: '4px 12px', borderRadius: '20px', fontSize: '.72rem', fontWeight: 700 }}>{ss.label}</span>
          <button onClick={handleExportPDF}  style={{ ...backBtnStyle, borderColor: 'rgba(0,180,216,.3)', color: 'var(--blue)' }}>
            📄 PDF
          </button>
          {user?.role === 'owner' && insp.status === 'submitted' && (
            <button onClick={() => setReviewing(r=>!r)} style={{ background: 'var(--green)', border: 'none', borderRadius: '8px', padding: '7px 14px', color: '#000', fontWeight: 700, fontSize: '.8rem', cursor: 'pointer', fontFamily: 'inherit' }}>
              ✅ Review
            </button>
          )}
          {/* Edit button — manager on own submitted inspection */}
          {(user?.role === 'manager' && insp.submittedBy?._id === user.id && insp.status === 'submitted') && (
            <button onClick={() => navigate(`/inspection/${id}/edit`)} style={{ background: 'var(--amber)', border: 'none', borderRadius: '8px', padding: '7px 14px', color: '#000', fontWeight: 700, fontSize: '.8rem', cursor: 'pointer', fontFamily: 'inherit' }}>
              ✏️ Edit
            </button>
          )}
          {user?.role === 'owner' && (
            <button onClick={handleDelete} style={{ background: 'transparent', border: '1px solid rgba(239,68,68,.3)', color: 'var(--red)', borderRadius: '8px', padding: '7px 14px', fontSize: '.78rem', cursor: 'pointer', fontFamily: 'inherit' }}>
              🗑
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: '780px', margin: '0 auto', padding: '22px 16px 60px' }}>

        {error && <div style={{ background: 'var(--red-bg)', border: '1px solid rgba(239,68,68,.3)', borderRadius: '9px', padding: '12px 16px', color: 'var(--red)', marginBottom: '16px', fontSize: '.84rem' }}>{error}</div>}

        {/* Owner review panel */}
        {reviewing && (
          <div style={{ background: '#ffffff', border: '1px solid rgba(34,197,94,.25)', borderRadius: '14px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ color: 'var(--green)', fontWeight: 700, fontSize: '.9rem', marginBottom: '14px' }}>✅ Owner Review</div>
            <div style={{ marginBottom: '12px' }}>
              <label style={lblStyle}>Notes / Feedback (optional)</label>
              <textarea value={ownerNotes} onChange={e => setOwnerNotes(e.target.value)}
                placeholder="Add any notes or feedback for the manager..."
                rows={3} style={textareaStyle}/>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => handleReview('reviewed')} disabled={saving} style={{ flex: 1, background: 'var(--green)', border: 'none', borderRadius: '9px', padding: '10px', color: '#000', fontWeight: 700, fontSize: '.88rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                {saving ? 'Saving...' : '✅ Mark as Reviewed'}
              </button>
              <button onClick={() => handleReview('archived')} disabled={saving} style={{ flex: 1, background: 'var(--border)', border: '1px solid var(--border-md)', borderRadius: '9px', padding: '10px', color: 'var(--muted)', fontWeight: 600, fontSize: '.88rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                📁 Archive
              </button>
              <button onClick={() => setReviewing(false)} style={{ background: 'transparent', border: '1px solid var(--border-md)', borderRadius: '9px', padding: '10px 16px', color: 'var(--muted)', fontSize: '.88rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Owner notes display */}
        {insp.ownerNotes && (
          <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '12px', padding: '14px 18px', marginBottom: '16px' }}>
            <div style={{ color: 'var(--green)', fontWeight: 700, fontSize: '.78rem', marginBottom: '6px' }}>👑 Owner Notes</div>
            <div style={{ color: 'var(--text)', fontSize: '.85rem', lineHeight: 1.6 }}>{insp.ownerNotes}</div>
            {insp.reviewedAt && <div style={{ color: 'var(--muted)', fontSize: '.7rem', marginTop: '6px' }}>Reviewed {new Date(insp.reviewedAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>}
          </div>
        )}

        {/* Meta info card */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-md)', borderRadius: '14px', padding: '18px', marginBottom: '18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          {[
            ['Manager',   insp.managerName],
            ['Email',     insp.managerEmail],
            ['Site',      insp.site],
            ['Submitted', new Date(insp.submittedAt).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })],
          ].map(([k,v]) => (
            <div key={k}>
              <div style={{ fontSize: '.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: 'var(--muted)', marginBottom: '3px' }}>{k}</div>
              <div style={{ fontSize: '.88rem', color: 'var(--text)', fontWeight: 500 }}>{v}</div>
            </div>
          ))}
        </div>

        {/* All 5 sections */}
        <Section icon="👔" title="Staff Presentation" items={STAFF_ITEMS}   data={insp.staffPresentation} type="tri" photos={insp.staffPresentation?.photos} onPhotoClick={openLightbox}/>
        <Section icon="🧹" title="Site Cleaning"      items={CLEANING_ITEMS} data={insp.siteCleaning}     type="bin" photos={insp.siteCleaning?.photos}     onPhotoClick={openLightbox}/>
        <Section icon="⭐" title="Quality"             items={QUALITY_ITEMS}  data={insp.quality}          type="bin" photos={insp.quality?.photos}           onPhotoClick={openLightbox}/>
        <Section icon="💬" title="Manager's Feedback" items={MGR_ITEMS}       data={insp.managerFeedback}  type="tri" photos={insp.managerFeedback?.photos}   onPhotoClick={openLightbox}/>

        {/* Suggestions */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-md)', borderRadius: '14px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ background: 'rgba(0,180,216,0.05)', borderBottom: '1px solid var(--border-md)', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '30px', height: '30px', background: 'var(--cyan)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.95rem' }}>💡</div>
            <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: '.9rem' }}>Suggestions & Review</div>
          </div>
          <div style={{ padding: '16px 18px' }}>
            {insp.suggestions?.text ? (
              <p style={{ color: 'var(--text)', fontSize: '.88rem', lineHeight: 1.7 }}>{insp.suggestions.text}</p>
            ) : (
              <p style={{ color: 'var(--muted)', fontSize: '.84rem', fontStyle: 'italic' }}>No suggestions provided.</p>
            )}
            {insp.suggestions?.photos?.length > 0 && (
              <div style={{ marginTop: '12px', borderTop: '1px dashed var(--border)', paddingTop: '12px' }}>
                <div style={{ fontSize: '.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: 'var(--muted)', marginBottom: '8px' }}>📸 Photos ({insp.suggestions.photos.length})</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {insp.suggestions.photos.map((p, i) => (
                    <img key={i} src={p.url} alt="" onClick={() => openLightbox(insp.suggestions.photos, i)}
                      style={{ width: '70px', height: '70px', borderRadius: '8px', objectFit: 'cover', border: '1.5px solid var(--border)', cursor: 'pointer' }}/>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

const LoadingScreen = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background:'var(--bg)', color: 'var(--blue)', fontFamily: "'Inter', sans-serif" }}>Loading inspection...</div>
);
const ErrorScreen = ({ error, onBack }) => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background:'var(--bg)', fontFamily: "'Inter', sans-serif", gap: '16px' }}>
    <div style={{ color: 'var(--red)', fontSize: '1rem' }}>{error}</div>
    <button onClick={onBack} style={{ background: '#ffffff', border: '1px solid var(--border-md)', color: 'var(--text)', borderRadius: '8px', padding: '8px 18px', cursor: 'pointer', fontFamily: 'inherit' }}>← Go Back</button>
  </div>
);

const backBtnStyle = { background: 'var(--bg)', border: '1px solid var(--border-md)', color: 'var(--muted)', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontSize: '.8rem', fontFamily: 'inherit' };
const lbBtnStyle   = { background: 'var(--bg)', border: '1px solid var(--border-md)', color: 'var(--text)', borderRadius: '8px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1rem' };
const lblStyle     = { display: 'block', fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: 'var(--muted)', marginBottom: '6px' };
const textareaStyle= { width: '100%', background: 'var(--bg)', border: '1.5px solid var(--border)', borderRadius: '8px', padding: '9px 13px', color: 'var(--text)', fontFamily: "'Inter', sans-serif", fontSize: '.88rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' };
